import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import { ScrollArea } from "../components/ui/scroll-area";
import { 
  Shield, 
  LogOut, 
  Users, 
  Brain, 
  Keyboard, 
  Trash2,
  RefreshCw,
  CheckCircle,
  XCircle,
  Clock,
  Eye,
  X
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Helper function to format time
const formatDuration = (seconds) => {
  if (!seconds && seconds !== 0) return "-";
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  if (mins === 0) return `${secs}d`;
  return `${mins}m ${secs}d`;
};

export default function AdminDashboardPage() {
  const navigate = useNavigate();
  const [participants, setParticipants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [admin, setAdmin] = useState(null);
  const [selectedParticipant, setSelectedParticipant] = useState(null);
  const [participantDetail, setParticipantDetail] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);

  const getAuthHeaders = () => {
    const token = localStorage.getItem("admin_token");
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  const fetchParticipantDetail = async (participantId) => {
    setLoadingDetail(true);
    try {
      const response = await axios.get(`${API}/admin/participants/${participantId}`, {
        withCredentials: true,
        headers: getAuthHeaders()
      });
      setParticipantDetail(response.data);
    } catch (error) {
      toast.error("Gagal memuat detail peserta");
    } finally {
      setLoadingDetail(false);
    }
  };

  const handleRowClick = (participant) => {
    setSelectedParticipant(participant);
    fetchParticipantDetail(participant.id);
  };

  const closeDialog = () => {
    setSelectedParticipant(null);
    setParticipantDetail(null);
  };

  useEffect(() => {
    const initDashboard = async () => {
      try {
        const response = await axios.get(`${API}/auth/me`, {
          withCredentials: true,
          headers: getAuthHeaders()
        });
        setAdmin(response.data);
        // Fetch participants after auth check
        const participantsRes = await axios.get(`${API}/admin/participants`, {
          withCredentials: true,
          headers: getAuthHeaders()
        });
        setParticipants(participantsRes.data);
      } catch (error) {
        toast.error("Sesi telah berakhir, silakan login kembali");
        localStorage.removeItem("admin_token");
        navigate("/admin");
      } finally {
        setLoading(false);
      }
    };
    
    initDashboard();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const checkAuth = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`, {
        withCredentials: true,
        headers: getAuthHeaders()
      });
      setAdmin(response.data);
      await fetchParticipants();
    } catch (error) {
      toast.error("Sesi telah berakhir, silakan login kembali");
      localStorage.removeItem("admin_token");
      navigate("/admin");
    }
  };

  const fetchParticipants = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/admin/participants`, {
        withCredentials: true,
        headers: getAuthHeaders()
      });
      setParticipants(response.data);
    } catch (error) {
      if (error.response?.status === 401) {
        toast.error("Sesi telah berakhir");
        localStorage.removeItem("admin_token");
        navigate("/admin");
      } else {
        toast.error("Gagal memuat data peserta");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, {
        withCredentials: true,
        headers: getAuthHeaders()
      });
    } catch (error) {
      // Ignore logout errors
    }
    localStorage.removeItem("admin_token");
    toast.success("Logout berhasil");
    navigate("/admin");
  };

  const handleDelete = async (participantId) => {
    if (!window.confirm("Yakin ingin menghapus peserta ini?")) return;
    
    try {
      await axios.delete(`${API}/admin/participants/${participantId}`, {
        withCredentials: true,
        headers: getAuthHeaders()
      });
      toast.success("Peserta berhasil dihapus");
      fetchParticipants();
    } catch (error) {
      toast.error("Gagal menghapus peserta");
    }
  };

  const completedCount = participants.filter(p => p.test_completed).length;
  const avgIQ = participants.length > 0 
    ? Math.round(participants.filter(p => p.iq_score).reduce((sum, p) => sum + (p.iq_score || 0), 0) / participants.filter(p => p.iq_score).length) || 0
    : 0;
  const avgWPM = participants.length > 0
    ? Math.round(participants.filter(p => p.wpm).reduce((sum, p) => sum + (p.wpm || 0), 0) / participants.filter(p => p.wpm).length * 10) / 10 || 0
    : 0;

  return (
    <div className="min-h-screen bg-[#F8F9FA]">
      {/* Header */}
      <header className="sticky top-0 z-10 bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-[#002FA7] flex items-center justify-center">
              <Shield className="w-5 h-5 text-white" />
            </div>
            <div>
              <h1 className="font-bold text-[#0A0A0A]">Admin Dashboard</h1>
              <p className="text-xs text-[#475569]">{admin?.email}</p>
            </div>
          </div>
          <Button
            variant="outline"
            onClick={handleLogout}
            className="rounded-sm"
            data-testid="btn-logout"
          >
            <LogOut className="w-4 h-4 mr-2" />
            Logout
          </Button>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 py-8">
        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <div className="bg-white border border-slate-200 p-4">
            <div className="flex items-center gap-2 mb-2">
              <Users className="w-4 h-4 text-[#002FA7]" />
              <span className="text-xs text-[#475569] uppercase tracking-wider">Total Peserta</span>
            </div>
            <p className="font-mono text-2xl font-bold text-[#0A0A0A]" data-testid="stat-total">
              {participants.length}
            </p>
          </div>
          
          <div className="bg-white border border-slate-200 p-4">
            <div className="flex items-center gap-2 mb-2">
              <CheckCircle className="w-4 h-4 text-green-600" />
              <span className="text-xs text-[#475569] uppercase tracking-wider">Test Selesai</span>
            </div>
            <p className="font-mono text-2xl font-bold text-green-600" data-testid="stat-completed">
              {completedCount}
            </p>
          </div>
          
          <div className="bg-white border border-slate-200 p-4">
            <div className="flex items-center gap-2 mb-2">
              <Brain className="w-4 h-4 text-[#002FA7]" />
              <span className="text-xs text-[#475569] uppercase tracking-wider">Rata-rata IQ</span>
            </div>
            <p className="font-mono text-2xl font-bold text-[#002FA7]" data-testid="stat-avg-iq">
              {avgIQ}
            </p>
          </div>
          
          <div className="bg-white border border-slate-200 p-4">
            <div className="flex items-center gap-2 mb-2">
              <Keyboard className="w-4 h-4 text-green-600" />
              <span className="text-xs text-[#475569] uppercase tracking-wider">Rata-rata WPM</span>
            </div>
            <p className="font-mono text-2xl font-bold text-green-600" data-testid="stat-avg-wpm">
              {avgWPM}
            </p>
          </div>
        </div>

        {/* Table */}
        <div className="bg-white border border-slate-200">
          <div className="p-4 border-b border-slate-200 flex items-center justify-between">
            <h2 className="font-bold text-[#0A0A0A]">Daftar Peserta</h2>
            <Button
              variant="outline"
              size="sm"
              onClick={fetchParticipants}
              disabled={loading}
              className="rounded-sm"
              data-testid="btn-refresh"
            >
              <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
          </div>

          {loading ? (
            <div className="p-8 text-center text-[#475569]">
              Memuat data...
            </div>
          ) : participants.length === 0 ? (
            <div className="p-8 text-center text-[#475569]">
              Belum ada peserta yang terdaftar
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50">
                    <TableHead className="font-semibold">Nama</TableHead>
                    <TableHead className="font-semibold">Email</TableHead>
                    <TableHead className="font-semibold">No. HP</TableHead>
                    <TableHead className="font-semibold">Usia</TableHead>
                    <TableHead className="font-semibold">Pendidikan</TableHead>
                    <TableHead className="font-semibold text-center">IQ Score</TableHead>
                    <TableHead className="font-semibold text-center">Waktu IQ</TableHead>
                    <TableHead className="font-semibold text-center">WPM</TableHead>
                    <TableHead className="font-semibold text-center">Waktu Ketik</TableHead>
                    <TableHead className="font-semibold text-center">Status</TableHead>
                    <TableHead className="font-semibold text-center">Aksi</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {participants.map((participant) => (
                    <TableRow 
                      key={participant.id} 
                      data-testid={`row-${participant.id}`}
                      className="cursor-pointer hover:bg-slate-50"
                      onClick={() => handleRowClick(participant)}
                    >
                      <TableCell className="font-medium">{participant.nama}</TableCell>
                      <TableCell className="text-[#475569]">{participant.email}</TableCell>
                      <TableCell className="text-[#475569]">{participant.no_hp}</TableCell>
                      <TableCell className="text-center">{participant.usia}</TableCell>
                      <TableCell>{participant.tingkat_pendidikan}</TableCell>
                      <TableCell className="text-center">
                        <span className="font-mono font-bold text-[#002FA7]">
                          {participant.iq_score || "-"}
                        </span>
                      </TableCell>
                      <TableCell className="text-center">
                        <span className="font-mono text-sm text-[#475569]">
                          {formatDuration(participant.iq_time_spent)}
                        </span>
                      </TableCell>
                      <TableCell className="text-center">
                        <span className="font-mono font-bold text-green-600">
                          {participant.wpm || "-"}
                        </span>
                      </TableCell>
                      <TableCell className="text-center">
                        <span className="font-mono text-sm text-[#475569]">
                          {formatDuration(participant.typing_time_spent)}
                        </span>
                      </TableCell>
                      <TableCell className="text-center">
                        {participant.test_completed ? (
                          <span className="inline-flex items-center gap-1 text-xs text-green-600 bg-green-50 px-2 py-1 rounded-sm">
                            <CheckCircle className="w-3 h-3" />
                            Selesai
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 text-xs text-orange-600 bg-orange-50 px-2 py-1 rounded-sm">
                            <XCircle className="w-3 h-3" />
                            Belum Selesai
                          </span>
                        )}
                      </TableCell>
                      <TableCell className="text-center">
                        <div className="flex items-center justify-center gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleRowClick(participant);
                            }}
                            className="text-[#002FA7] hover:text-[#00237C] hover:bg-blue-50"
                            data-testid={`btn-view-${participant.id}`}
                          >
                            <Eye className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDelete(participant.id);
                            }}
                            className="text-red-500 hover:text-red-700 hover:bg-red-50"
                            data-testid={`btn-delete-${participant.id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </div>
      </main>

      {/* Participant Detail Dialog */}
      <Dialog open={!!selectedParticipant} onOpenChange={closeDialog}>
        <DialogContent className="max-w-4xl max-h-[90vh] p-0">
          <DialogHeader className="p-6 pb-4 border-b border-slate-200">
            <div className="flex items-center justify-between">
              <DialogTitle className="text-xl font-bold text-[#0A0A0A]">
                Detail Jawaban Test IQ
              </DialogTitle>
            </div>
            {selectedParticipant && (
              <div className="mt-2 flex flex-wrap gap-4 text-sm">
                <span className="text-[#475569]">
                  Peserta: <span className="font-semibold text-[#0A0A0A]">{selectedParticipant.nama}</span>
                </span>
                <span className="text-[#475569]">
                  Skor IQ: <span className="font-mono font-bold text-[#002FA7]">{selectedParticipant.iq_score || "-"}</span>
                </span>
                {participantDetail?.correct_count !== undefined && (
                  <span className="text-[#475569]">
                    Benar: <span className="font-semibold text-green-600">{participantDetail.correct_count}/30</span>
                  </span>
                )}
              </div>
            )}
          </DialogHeader>

          <ScrollArea className="h-[60vh] p-6">
            {loadingDetail ? (
              <div className="flex items-center justify-center py-12">
                <Brain className="w-8 h-8 text-[#002FA7] animate-pulse" />
                <span className="ml-2 text-[#475569]">Memuat data...</span>
              </div>
            ) : participantDetail?.iq_answers && participantDetail.iq_answers.length > 0 ? (
              <div className="space-y-4">
                {participantDetail.iq_answers.map((answer, index) => (
                  <div
                    key={answer.question_id}
                    className={`p-4 border rounded-sm ${
                      answer.is_correct
                        ? "border-green-200 bg-green-50/50"
                        : "border-red-200 bg-red-50/50"
                    }`}
                    data-testid={`answer-${answer.question_id}`}
                  >
                    <div className="flex items-start gap-3">
                      <span className={`flex-shrink-0 w-8 h-8 flex items-center justify-center rounded-sm font-mono font-bold text-sm ${
                        answer.is_correct
                          ? "bg-green-100 text-green-700"
                          : "bg-red-100 text-red-700"
                      }`}>
                        {answer.question_id}
                      </span>
                      <div className="flex-1">
                        <p className="font-medium text-[#0A0A0A] mb-3">
                          {answer.question_text}
                        </p>
                        
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                          {answer.options?.map((option, optIdx) => {
                            const isUserAnswer = option === answer.user_answer;
                            const isCorrectAnswer = option === answer.correct_answer;
                            
                            let optionClass = "p-2 rounded-sm text-sm border ";
                            
                            if (isCorrectAnswer) {
                              optionClass += "border-green-400 bg-green-100 text-green-800";
                            } else if (isUserAnswer && !answer.is_correct) {
                              optionClass += "border-red-400 bg-red-100 text-red-800 line-through";
                            } else {
                              optionClass += "border-slate-200 bg-white text-[#475569]";
                            }
                            
                            return (
                              <div key={optIdx} className={optionClass}>
                                <div className="flex items-center gap-2">
                                  <span className="font-mono text-xs opacity-60">
                                    {String.fromCharCode(65 + optIdx)}.
                                  </span>
                                  <span>{option}</span>
                                  {isCorrectAnswer && (
                                    <CheckCircle className="w-4 h-4 text-green-600 ml-auto" />
                                  )}
                                  {isUserAnswer && !answer.is_correct && (
                                    <XCircle className="w-4 h-4 text-red-600 ml-auto" />
                                  )}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                        
                        <div className="mt-3 flex flex-wrap gap-4 text-xs">
                          <span className={answer.is_correct ? "text-green-600" : "text-red-600"}>
                            Jawaban Anda: <span className="font-semibold">{answer.user_answer}</span>
                          </span>
                          {!answer.is_correct && (
                            <span className="text-green-600">
                              Jawaban Benar: <span className="font-semibold">{answer.correct_answer}</span>
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center py-12 text-[#475569]">
                <Brain className="w-12 h-12 mb-4 opacity-30" />
                <p>Peserta belum mengerjakan test IQ</p>
              </div>
            )}
          </ScrollArea>

          <div className="p-4 border-t border-slate-200 flex justify-end">
            <Button
              variant="outline"
              onClick={closeDialog}
              className="rounded-sm"
              data-testid="btn-close-dialog"
            >
              Tutup
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
